import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import IngresoModal from './IngresoModal';
import { TrashIcon, EyeIcon, EyeOffIcon } from '../components/Icons';
import { onDataChanged } from '../utils/dataBus';

const formatMoneyRaw = (n) => '$' + (n || 0).toLocaleString('es-AR');
const MASK = '$ ••••';
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
const currentMonthKey = () => new Date().toISOString().substring(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split('-');
  const l = new Date(+y, +m - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  return l.charAt(0).toUpperCase() + l.slice(1);
};

export default function IngresosDrawer({ open, onClose, onSaved, initialMes = null, ocultar = false }) {
  const [ingresos, setIngresos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [mesFiltro, setMesFiltro] = useState(currentMonthKey());
  const [ocultarLocal, setOcultarLocal] = useState(ocultar);

  const formatMoney = (n) => ocultarLocal ? MASK : formatMoneyRaw(n);

  const showToast = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 2200); };

  const fetchIngresos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('ingresos')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula)')
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false });
    setIngresos(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchIngresos();
      setMesFiltro(initialMes || currentMonthKey());
      setOcultarLocal(ocultar);
    } else {
      setModalOpen(false);
      setEditing(null);
    }
  }, [open, fetchIngresos, initialMes, ocultar]);

  // Mientras está abierto, refrescar ante cualquier escritura en ingresos
  // (registrar un cobro desde deudas, el bot, otra pestaña, etc.).
  useEffect(() => {
    if (!open) return;
    return onDataChanged(() => fetchIngresos(), 'ingresos');
  }, [open, fetchIngresos]);

  function abrirNuevo() { setEditing(null); setModalOpen(true); }
  function abrirEdicion(i) { setEditing(i); setModalOpen(true); }

  async function eliminar(i) {
    if (!window.confirm('¿Eliminar este ingreso?')) return;
    // RPC SECURITY DEFINER: el UPDATE directo desde el cliente fallaba en
    // silencio por el WITH CHECK de RLS (mismo patrón que tramites).
    const { error } = await supabase.rpc('eliminar_ingreso', { p_ingreso_id: i.id });
    if (error) { showToast('Error al eliminar', 'error'); return; }
    showToast('Ingreso eliminado', 'error');
    fetchIngresos();
    onSaved?.();
  }

  function handleSaved() {
    fetchIngresos();
    onSaved?.();
  }

  if (!open) return null;

  const mesAct = currentMonthKey();
  const totalMes = ingresos.filter(i => (i.fecha || '').startsWith(mesAct)).reduce((s, i) => s + (i.monto || 0), 0);

  // meses presentes en los datos (desc), excluyendo el actual
  const mesesPrevios = [...new Set(ingresos.map(i => (i.fecha || '').substring(0, 7)).filter(k => k && k !== mesAct))].sort().reverse();

  const visibles = mesFiltro === 'todos'
    ? ingresos
    : ingresos.filter(i => (i.fecha || '').startsWith(mesFiltro));

  const grupos = (() => {
    const map = {};
    visibles.forEach(i => { const k = (i.fecha || '').substring(0, 7) || 'sin'; (map[k] = map[k] || []).push(i); });
    return Object.keys(map).sort().reverse().map(k => ({ key: k, label: k === 'sin' ? 'Sin fecha' : monthLabel(k), items: map[k] }));
  })();

  const renderRow = (i) => {
    const cliente = i.clientes ? `${i.clientes.apellido || ''} ${i.clientes.nombre || ''}`.trim() : i.cliente_nombre || '';
    const medioBg = { Efectivo: '#dcfce7', Transferencia: '#dbeafe' }[i.medio_cobro] || '#f3f4f6';
    const medioColor = { Efectivo: '#15803d', Transferencia: '#1d4ed8' }[i.medio_cobro] || '#6b7280';
    return (
      <div
        key={i.id}
        onClick={() => abrirEdicion(i)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.concepto || '—'}</div>
          <div style={{ fontSize: 11, color: 'var(--text4)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
            {cliente && <><span>{cliente}</span><span>·</span></>}
            <span>{formatDate(i.fecha)}</span>
            <span style={{ background: medioBg, color: medioColor, padding: '0 6px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{i.medio_cobro}</span>
            {i.expedientes && <><span>·</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{i.expedientes.numero || i.expedientes.caratula}</span></>}
          </div>
        </div>
        <div style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 700, color: '#22c55e', flexShrink: 0 }}>{formatMoney(i.monto)}</div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={(e) => { e.stopPropagation(); eliminar(i); }}
          style={{ flexShrink: 0, color: 'var(--text4)', display: 'flex', alignItems: 'center' }}
          title="Eliminar"
        ><TrashIcon /></button>
      </div>
    );
  };

  return (
    <>
      <div className="drawer-overlay" onMouseDown={onClose} />
      <div className="drawer drawer-wide">

        <div className="drawer-header">
          <div className="drawer-header-top">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div>
                <div className="drawer-num">CONTABILIDAD</div>
                <div className="drawer-title">Ingresos</div>
              </div>
              <button
                onClick={() => setOcultarLocal(v => !v)}
                title={ocultarLocal ? 'Mostrar cifras' : 'Ocultar cifras'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0, padding: 4, color: 'var(--text4)', display: 'flex', alignItems: 'center' }}
              >
                {ocultarLocal ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
              </button>
            </div>
            <button className="drawer-close" onClick={onClose}>✕</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', gap: 18 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Este mes</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 16, fontWeight: 700, color: '#22c55e' }}>{formatMoney(totalMes)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Registros (mes)</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{ingresos.filter(i => (i.fecha || '').startsWith(mesAct)).length}</div>
              </div>
            </div>
            <button className="btn btn-primary" onClick={abrirNuevo}>+ Nuevo ingreso</button>
          </div>
        </div>

        <div className="drawer-body">

          {/* Filtro de mes */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
              {mesFiltro === 'todos' ? 'Todos los meses' : monthLabel(mesFiltro)}
              <span style={{ color: 'var(--text4)', fontWeight: 400 }}> · {visibles.length}</span>
            </span>
            <select
              className="form-select"
              value={mesFiltro}
              onChange={e => setMesFiltro(e.target.value)}
              style={{ width: 'auto', fontSize: 12, padding: '5px 10px' }}
            >
              <option value={mesAct}>Este mes</option>
              {mesesPrevios.map(k => <option key={k} value={k}>{monthLabel(k)}</option>)}
              <option value="todos">Todos los meses</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>Cargando…</div>
          ) : visibles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>
              {mesFiltro === 'todos' ? 'No hay ingresos registrados.' : 'Sin ingresos en este mes.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {grupos.map(g => (
                <div key={g.key}>
                  {mesFiltro === 'todos' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, paddingLeft: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'capitalize', letterSpacing: 0.4 }}>{g.label}</span>
                      <span style={{ fontSize: 11, fontFamily: 'DM Mono', fontWeight: 700, color: '#22c55e' }}>{formatMoney(g.items.reduce((s, x) => s + (x.monto || 0), 0))}</span>
                    </div>
                  )}
                  <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {g.items.map(i => renderRow(i))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {toast && (
          <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', background: toast.tipo === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.tipo === 'error' ? '#b91c1c' : '#15803d', border: `0.5px solid ${toast.tipo === 'error' ? '#fecaca' : '#bbf7d0'}`, padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            {toast.msg}
          </div>
        )}
      </div>

      <IngresoModal
        open={modalOpen}
        editing={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={handleSaved}
      />
    </>
  );
}
